import { parseISO } from "date-fns";
import { Check, ChevronLeft, Copy, Pencil, Power, PowerOff } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { FirebaseUserRaw, UserDetailUI } from "~/features/admin/users/types";
import { FirebaseUserSection } from "./FirebaseUserSection";

interface UserDetailPresenterProps {
  loginPreparationPending?: boolean;
  preparingLogin?: boolean;
  onRetryLoginPreparation?: () => void;
  user: UserDetailUI;
  firebaseUser: FirebaseUserRaw | null;
  onBack: () => void;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  pendingAction: "activate" | "deactivate" | null;
  confirmLabel: string;
  processing: boolean;
  onConfirm: () => void;
  onCancelConfirm: () => void;
}

export function UserDetailPresenter({
  loginPreparationPending = false,
  preparingLogin = false,
  onRetryLoginPreparation,
  user,
  firebaseUser,
  onBack,
  onEdit,
  onActivate,
  onDeactivate,
  pendingAction,
  confirmLabel,
  processing,
  onConfirm,
  onCancelConfirm,
}: UserDetailPresenterProps) {
  const { t } = useTranslation();

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("model.user.displayName"), value: user.displayName || "—" },
    { label: t("model.user.email"), value: user.email || "—" },
    {
      label: t("model.user.organization"),
      value: (
        <span className="inline-flex items-baseline gap-1.5">
          <span>{user.organization.name}</span>
          <span className="font-mono text-12-regular text-fg-3">{user.organization.shortCode}</span>
        </span>
      ),
    },
    ...user.productRoles.map((pr) => ({ label: pr.productName, value: pr.roles.join(" / ") })),
    { label: t("model.user.segments"), value: user.segments.join(" / ") || "—" },
    { label: t("model.user.groups"), value: user.groups.join(" / ") || "—" },
    { label: t("model.user.memo"), value: <span className="whitespace-pre-line">{user.memo}</span> },
  ];

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ChevronLeft />
        <span>{t("ui.backToList")}</span>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-dewey-title">{user.displayName || user.email}</h1>
          {user.deactivatedAt && <Badge tone="default">{t("ui.deactivated")}</Badge>}
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
          {user.deactivatedAt ? (
            <Button type="button" variant="ghost" size="sm" onClick={onActivate} disabled={processing}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onDeactivate} disabled={processing}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      <SubHeader user={user} />

      {loginPreparationPending && (
        <section className="space-y-2 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-4">
          <p className="text-14-regular text-fg-1">{t("feature.users.loginPreparationPending")}</p>
          <Button type="button" variant="outline" size="sm" disabled={preparingLogin} onClick={onRetryLoginPreparation}>
            {t("feature.users.retryLoginPreparation")}
          </Button>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-16-bold leading-tight capitalize">{t("model.user.modelName")}</h2>
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
          <table className="w-full border-collapse">
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border-default" : undefined}>
                  <th className="w-44 bg-bg-subtle px-3.5 py-2 text-left text-12-medium text-fg-2 align-top">
                    {row.label}
                  </th>
                  <td className="px-3.5 py-2 text-14-regular text-fg-1">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {firebaseUser && <FirebaseUserSection firebaseUser={firebaseUser} />}

      <AlertDialog open={pendingAction !== null} onOpenChange={(next) => !next && onCancelConfirm()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("feature.common.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{confirmLabel}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirm} disabled={processing}>
              {pendingAction === "activate" ? t("ui.activate") : t("ui.deactivate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubHeader({ user }: { user: UserDetailUI }) {
  const { t } = useTranslation();
  const items: { label: string; value: React.ReactNode }[] = [
    { label: t("model.common.id"), value: <CopyableText text={user.id} className="font-mono" /> },
  ];
  if (user.createdAt) {
    items.push({ label: t("model.common.createdAt"), value: t("date.long", { date: parseISO(user.createdAt) }) });
  }
  if (user.updatedAt && user.updatedAt !== user.createdAt) {
    items.push({ label: t("model.common.updatedAt"), value: t("date.long", { date: parseISO(user.updatedAt) }) });
  }
  if (user.deactivatedAt) {
    items.push({
      label: t("model.common.deactivatedAt"),
      value: t("date.long", { date: parseISO(user.deactivatedAt) }),
    });
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-12-regular text-fg-3">
      {items.map((item, i) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          {i > 0 && (
            <span aria-hidden className="text-border-strong">
              |
            </span>
          )}
          <span className="font-medium uppercase tracking-[0.04em]">{item.label}</span>
          <span className="text-fg-2 tabular-nums">{item.value}</span>
        </span>
      ))}
    </div>
  );
}

function CopyableText({ text, className }: { text: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success(t("feature.common.idCopied"));
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={className}>{text}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-xs)] text-fg-3 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={t("ui.copyId")}
          >
            {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("ui.copyId")}</TooltipContent>
      </Tooltip>
    </span>
  );
}
